import { useCallback, useRef } from 'react'

import { useAppSelector, useAppDispatch } from 'store/store'

import * as actions from './actions'
import {
  MatchWinnerModalParamsRequired,
  MatchWinnerRequestParams,
  TeamScoreModalParamsRequired,
  TeamScoreRequestParams,
  TournamentParams,
} from './types'

// Request deduplication - prevents rapid repeated calls to the same endpoint
const pendingRequests = new Map<string, number>()
const REQUEST_THROTTLE_MS = 500 // Minimum time between identical requests

export const useTournament = () => {
  const dispatch = useAppDispatch()
  const tournamentState = useAppSelector(({ tournament }) => tournament)

  const getAllTournaments = useCallback(
    (status: string) => {
      dispatch(actions.getAllTournaments(status))
    },
    [dispatch],
  )

  const getCommunityTournaments = useCallback(
    (communityId: string) => {
      return dispatch(actions.getCommunityTournaments(communityId))
    },
    [dispatch],
  )

  const createTournament = useCallback(
    (payload: TournamentParams) => {
      return dispatch(actions.createTournament(payload))
    },
    [dispatch],
  )

  const openTeamScoreModal = useCallback(
    (payload: TeamScoreModalParamsRequired) => {
      dispatch(actions.teamScoreModalOpen(payload))
    },
    [dispatch],
  )

  const closeTeamScoreModal = useCallback(() => {
    dispatch(actions.teamScoreModalClose())
  }, [dispatch])

  const setTeamScore = useCallback(
    (payload: TeamScoreRequestParams) => {
      dispatch(actions.setTeamScore(payload))
    },
    [dispatch],
  )

  const openMatchWinnerModal = useCallback(
    (payload: MatchWinnerModalParamsRequired) => {
      dispatch(actions.matchWinnerModalOpen(payload))
    },
    [dispatch],
  )

  const closeMatchWinnerModal = useCallback(() => {
    dispatch(actions.matchWinnerModalClose())
  }, [dispatch])

  const setMatchWinner = useCallback(
    (payload: MatchWinnerRequestParams) => {
      dispatch(actions.setMatchWinner(payload))
    },
    [dispatch],
  )
  const updateTournamentStatus = useCallback(
    (payload: { tournamentId: string; status: string; token: string }) => {
      dispatch(actions.updateTournamentStatus(payload))
    },
    [dispatch],
  )
  const getTournament = useCallback(
    (tournamentId: string) => {
      // Deduplicate rapid requests to the same tournament
      const requestKey = `getTournament_${tournamentId}`
      const now = Date.now()
      const lastRequest = pendingRequests.get(requestKey)

      if (lastRequest && now - lastRequest < REQUEST_THROTTLE_MS) {
        // Skip this request - too soon after previous identical request
        return
      }

      pendingRequests.set(requestKey, now)
      dispatch(actions.getTournamentById(tournamentId))
    },
    [dispatch],
  )
  const deleteTournament = useCallback(
    (tournamentId: string) => {
      dispatch(actions.deleteTournament(tournamentId))
    },
    [dispatch],
  )

  const retryTournamentDeletion = useCallback(
    (tournamentId: string) => {
      dispatch(actions.retryTournamentDeletion(tournamentId))
    },
    [dispatch],
  )

  const startGroupPlay = useCallback(
    (payload: { tournamentId: string; config?: { teamsPerGroup?: number; playInEnabled?: boolean } }) => {
      return dispatch(actions.startGroupPlay(payload))
    },
    [dispatch],
  )

  return {
    ...tournamentState,
    getAllTournaments,
    getCommunityTournaments,
    createTournament,
    openTeamScoreModal,
    closeTeamScoreModal,
    setTeamScore,
    openMatchWinnerModal,
    closeMatchWinnerModal,
    setMatchWinner,
    updateTournamentStatus,
    getTournament,
    deleteTournament,
    retryTournamentDeletion,
    startGroupPlay,
  }
}
